import { createFileRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';
import { useAdminUsersQuery } from '@/api/admin-users/getAdminUsers';
import { useCreateAdminUserMutation } from '@/api/admin-users/createAdminUser';
import { useUpdateAdminUserMutation } from '@/api/admin-users/updateAdminUser';
import { useDeleteAdminUserMutation } from '@/api/admin-users/deleteAdminUser';
import { isSuperAdmin } from '@/utils/auth';
import { useAllowedTenants } from '@/hooks/useAllowedTenants';
import { GAME_TYPES } from '@low6-pickem/sports-config';
import { Plus, Pencil, Trash2, Shield, ShieldCheck } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { TzDateDisplay } from '@/components/datetime/TzDateDisplay';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { PageContainer } from '@/components/layout/PageContainer';
import { ContentCard } from '@/components/layout/ContentCard';
import { Breadcrumbs } from '@/components/layout/Breadcrumbs';
import { toast } from 'sonner';
import { AdminRole } from '@low6-pickem/api-types';
import type { AdminUser } from '@/api/admin-users/getAdminUsers';

export const Route = createFileRoute('/dashboard/admin-users/')({
  beforeLoad: () => {
    if (!isSuperAdmin()) {
      throw redirect({
        to: '/dashboard',
      });
    }
  },
  component: AdminUsersPage,
});

const isAdminRole = (value: string): value is AdminRole =>
  value === AdminRole.SUPER_ADMIN || value === AdminRole.GAME_MANAGER || value === AdminRole.MEMBER;

function AdminUsersPage() {
  const [includeInactive, setIncludeInactive] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: '',
    role: AdminRole.GAME_MANAGER,
    tenantIds: [] as string[],
    gameTypes: [] as string[],
  });

  const { tenants, isSingleTenant, autoSelectedTenantId } = useAllowedTenants();

  const { data: adminUsers = [], isLoading, refetch } = useAdminUsersQuery(includeInactive);
  const createMutation = useCreateAdminUserMutation();
  const updateMutation = useUpdateAdminUserMutation();
  const deleteMutation = useDeleteAdminUserMutation();

  const handleRoleChange = (role: AdminRole) => {
    const tenantIds =
      isSingleTenant && autoSelectedTenantId && role !== AdminRole.SUPER_ADMIN
        ? [autoSelectedTenantId]
        : [];
    setFormData({ ...formData, role, tenantIds, gameTypes: [] });
  };

  const handleTenantToggle = (tenantId: string) => {
    const current = formData.tenantIds;
    setFormData({
      ...formData,
      tenantIds: current.includes(tenantId)
        ? current.filter(id => id !== tenantId)
        : [...current, tenantId],
    });
  };

  const handleGameTypeToggle = (gameType: string) => {
    const current = formData.gameTypes;
    setFormData({
      ...formData,
      gameTypes: current.includes(gameType)
        ? current.filter(g => g !== gameType)
        : [...current, gameType],
    });
  };

  const handleCreate = async () => {
    try {
      await createMutation.mutateAsync({
        email: formData.email,
        password: formData.password,
        name: formData.name || undefined,
        role: formData.role,
        tenantIds: formData.role !== AdminRole.SUPER_ADMIN ? formData.tenantIds : undefined,
        gameTypes: formData.role === AdminRole.MEMBER ? formData.gameTypes : undefined,
      });
      toast.success('Admin user created successfully');
      setCreateDialogOpen(false);
      resetForm();
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to create admin user');
    }
  };

  const handleEdit = (user: AdminUser) => {
    setSelectedUser(user);
    setFormData({
      email: user.email,
      password: '',
      name: user.name || '',
      role: isAdminRole(user.role) ? user.role : AdminRole.GAME_MANAGER,
      tenantIds: user.permissions?.filter(p => p.tenantId).map(p => p.tenantId!) ?? [],
      gameTypes: user.permissions?.filter(p => p.gameType).map(p => p.gameType!) ?? [],
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedUser) return;

    try {
      const updateData: any = {
        email: formData.email,
        name: formData.name || undefined,
        role: formData.role,
        tenantIds: formData.role !== AdminRole.SUPER_ADMIN ? formData.tenantIds : undefined,
        gameTypes: formData.role === AdminRole.MEMBER ? formData.gameTypes : undefined,
      };
      if (formData.password) {
        updateData.password = formData.password;
      }
      await updateMutation.mutateAsync({
        id: selectedUser.id,
        data: updateData,
      });
      toast.success('Admin user updated successfully');
      setEditDialogOpen(false);
      resetForm();
      setSelectedUser(null);
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update admin user');
    }
  };

  const handleDeleteClick = (user: AdminUser) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedUser) return;

    try {
      await deleteMutation.mutateAsync(selectedUser.id);
      toast.success('Admin user deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete admin user');
    }
  };

  const handleToggleActive = async (user: AdminUser) => {
    try {
      await updateMutation.mutateAsync({
        id: user.id,
        data: { isActive: !user.isActive },
      });
      toast.success(`Admin user ${!user.isActive ? 'activated' : 'deactivated'} successfully`);
      refetch();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update admin user');
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      password: '',
      name: '',
      role: AdminRole.GAME_MANAGER,
      tenantIds: [],
      gameTypes: [],
    });
  };

  return (
    <>
      <PageContainer>
        <Breadcrumbs overrides={{ 'admin-users': 'Admin Users' }} />

        <div className="flex gap-4 items-center mb-6 flex-wrap">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-gray-500" />
            <h1 className="text-2xl font-semibold">Admin Users</h1>
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <div className="flex items-center gap-2">
              <Switch
                id="include-inactive"
                checked={includeInactive}
                onCheckedChange={setIncludeInactive}
              />
              <Label htmlFor="include-inactive">Include Inactive</Label>
            </div>

            <Button
              className="bg-purple-600 hover:bg-purple-700 text-white"
              onClick={() => {
                resetForm();
                setCreateDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Admin User
            </Button>
          </div>
        </div>

        <ContentCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Email</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell>
                      <Skeleton className="h-4 w-48" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-24" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-16" />
                    </TableCell>
                    <TableCell>
                      <Skeleton className="h-4 w-32" />
                    </TableCell>
                    <TableCell className="text-right">
                      <Skeleton className="h-8 w-16 ml-auto" />
                    </TableCell>
                  </TableRow>
                ))
              ) : adminUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                    No admin users found
                  </TableCell>
                </TableRow>
              ) : (
                adminUsers.map(user => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.email}</TableCell>
                    <TableCell>{user.name || '-'}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          user.role === AdminRole.SUPER_ADMIN
                            ? 'default'
                            : user.role === AdminRole.GAME_MANAGER
                              ? 'outline'
                              : 'secondary'
                        }
                      >
                        {user.role === AdminRole.SUPER_ADMIN && (
                          <ShieldCheck className="mr-1 h-3 w-3" />
                        )}
                        {user.role === AdminRole.SUPER_ADMIN
                          ? 'Super Admin'
                          : user.role === AdminRole.GAME_MANAGER
                            ? 'Game Manager'
                            : 'Member'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.isActive ? 'default' : 'destructive'}>
                        {user.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <TzDateDisplay
                        utcDate={user.createdAt}
                        formatString="MMM d, yyyy h:mm a"
                        showUtcBadge={false}
                        showTimezoneTooltip={false}
                        dateOnly={false}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2 items-center">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={user.isActive}
                            onCheckedChange={() => handleToggleActive(user)}
                            title={user.isActive ? 'Deactivate' : 'Activate'}
                          />
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(user)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(user)}
                          title="Delete"
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </ContentCard>
      </PageContainer>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Admin User</DialogTitle>
            <DialogDescription>Create a new admin user account.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="create-email">Email *</Label>
              <Input
                id="create-email"
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
                placeholder="admin@example.com"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-password">Password *</Label>
              <Input
                id="create-password"
                type="password"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                placeholder="Enter password"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-name">Name</Label>
              <Input
                id="create-name"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
                placeholder="Full name (optional)"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="create-role">Role *</Label>
              <Select value={formData.role} onValueChange={handleRoleChange}>
                <SelectTrigger id="create-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AdminRole.SUPER_ADMIN}>Super Admin</SelectItem>
                  <SelectItem value={AdminRole.GAME_MANAGER}>Game Manager</SelectItem>
                  <SelectItem value={AdminRole.MEMBER}>Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.role !== AdminRole.SUPER_ADMIN && !isSingleTenant && (
              <div className="grid gap-2">
                <Label>Tenants</Label>
                <div className="flex flex-col gap-2 max-h-40 overflow-y-auto border rounded p-2">
                  {tenants.map(tenant => (
                    <div key={tenant.tenant_id} className="flex items-center gap-2">
                      <Checkbox
                        id={`create-tenant-${tenant.tenant_id}`}
                        checked={formData.tenantIds.includes(tenant.tenant_id)}
                        onCheckedChange={() => handleTenantToggle(tenant.tenant_id)}
                      />
                      <Label
                        htmlFor={`create-tenant-${tenant.tenant_id}`}
                        className="font-normal cursor-pointer"
                      >
                        {tenant.title || tenant.name_en || tenant.tenant_id}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {formData.role !== AdminRole.SUPER_ADMIN && isSingleTenant && (
              <div className="grid gap-2">
                <Label>Tenant</Label>
                <p className="text-sm text-gray-500">
                  {tenants[0]?.title || tenants[0]?.name_en || tenants[0]?.tenant_id}{' '}
                  (auto-selected)
                </p>
              </div>
            )}
            {formData.role === AdminRole.MEMBER && (
              <div className="grid gap-2">
                <Label>Game Types</Label>
                <div className="flex flex-col gap-2 border rounded p-2">
                  {GAME_TYPES.map(gt => (
                    <div key={gt.code} className="flex items-center gap-2">
                      <Checkbox
                        id={`create-game-${gt.code}`}
                        checked={formData.gameTypes.includes(gt.code)}
                        onCheckedChange={() => handleGameTypeToggle(gt.code)}
                      />
                      <Label
                        htmlFor={`create-game-${gt.code}`}
                        className="font-normal cursor-pointer"
                      >
                        {gt.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={!formData.email || !formData.password}>
              Create
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Admin User</DialogTitle>
            <DialogDescription>Update admin user information.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-email">Email *</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-password">New Password</Label>
              <Input
                id="edit-password"
                type="password"
                value={formData.password}
                onChange={e => setFormData({ ...formData, password: e.target.value })}
                placeholder="Leave blank to keep current password"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={e => setFormData({ ...formData, name: e.target.value })}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-role">Role *</Label>
              <Select value={formData.role} onValueChange={handleRoleChange}>
                <SelectTrigger id="edit-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={AdminRole.SUPER_ADMIN}>Super Admin</SelectItem>
                  <SelectItem value={AdminRole.GAME_MANAGER}>Game Manager</SelectItem>
                  <SelectItem value={AdminRole.MEMBER}>Member</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {formData.role !== AdminRole.SUPER_ADMIN && !isSingleTenant && (
              <div className="grid gap-2">
                <Label>Tenants</Label>
                <div className="flex flex-col gap-2 max-h-40 overflow-y-auto border rounded p-2">
                  {tenants.map(tenant => (
                    <div key={tenant.tenant_id} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-tenant-${tenant.tenant_id}`}
                        checked={formData.tenantIds.includes(tenant.tenant_id)}
                        onCheckedChange={() => handleTenantToggle(tenant.tenant_id)}
                      />
                      <Label
                        htmlFor={`edit-tenant-${tenant.tenant_id}`}
                        className="font-normal cursor-pointer"
                      >
                        {tenant.title || tenant.name_en || tenant.tenant_id}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {formData.role !== AdminRole.SUPER_ADMIN && isSingleTenant && (
              <div className="grid gap-2">
                <Label>Tenant</Label>
                <p className="text-sm text-gray-500">
                  {tenants[0]?.title || tenants[0]?.name_en || tenants[0]?.tenant_id}{' '}
                  (auto-selected)
                </p>
              </div>
            )}
            {formData.role === AdminRole.MEMBER && (
              <div className="grid gap-2">
                <Label>Game Types</Label>
                <div className="flex flex-col gap-2 border rounded p-2">
                  {GAME_TYPES.map(gt => (
                    <div key={gt.code} className="flex items-center gap-2">
                      <Checkbox
                        id={`edit-game-${gt.code}`}
                        checked={formData.gameTypes.includes(gt.code)}
                        onCheckedChange={() => handleGameTypeToggle(gt.code)}
                      />
                      <Label
                        htmlFor={`edit-game-${gt.code}`}
                        className="font-normal cursor-pointer"
                      >
                        {gt.label}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={!formData.email}>
              Update
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will deactivate the admin user account for {selectedUser?.email}. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
